import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Badge, Box, Button, Input, MultiSelectList, Radio, toast } from '@node-space/ui-components'
import { useUpdateHppPaymentMethodsMutation } from 'hooks/mutations/useUpdateHppPaymentMethodsMutation'
import { useUpdateHppRemoteWalletProvidersMutation } from 'hooks/mutations/useUpdateHppRemoteWalletProvidersMutation'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import {
  PaymentMethodFormValue,
  RemoteWalletProviderItem,
  type HppSettingsFormValues,
} from 'types/hppSettings'
import { formValueToPaymentMethods, usePaymentOptions, useWalletSections } from './utils'

interface HppSettingsFormProps {
  paymentMethod: PaymentMethodFormValue
  remoteWalletProviders: RemoteWalletProviderItem[]
}

export const HppSettingsForm = ({ paymentMethod, remoteWalletProviders }: HppSettingsFormProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const updatePaymentMethodsMutation = useUpdateHppPaymentMethodsMutation()
  const updateRemoteWalletProvidersMutation = useUpdateHppRemoteWalletProvidersMutation()

  const { watch, setValue } = useForm<HppSettingsFormValues>({
    defaultValues: { paymentMethod },
  })

  const selectedPaymentMethod = watch('paymentMethod')
  const showWallets = selectedPaymentMethod !== PaymentMethodFormValue.QR_CODE_ONLY

  const [searchTerm, setSearchTerm] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { walletSections, selectedWalletCount, sectionsWithCallbacks, handleSelectAllChange } =
    useWalletSections(remoteWalletProviders, searchTerm)
  const radioOptions = usePaymentOptions()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    const paymentMethods = formValueToPaymentMethods(selectedPaymentMethod)
    const actions = walletSections
      .flatMap(s => s.items)
      .map(item => ({
        type: 'PROVIDER_ENABLEMENT' as const,
        providerId: item.id,
        enabled: item.checked,
      }))

    try {
      await Promise.all([
        updatePaymentMethodsMutation.mutateAsync({ types: paymentMethods }),
        updateRemoteWalletProvidersMutation.mutateAsync({ actions }),
      ])
      toast({ variant: 'success', header: t('hppSettings.saveSuccess') })
      queryClient.invalidateQueries({ queryKey: reactQueryKeys.hppPaymentMethods() })
      queryClient.invalidateQueries({ queryKey: reactQueryKeys.hppRemoteWalletProviders() })
    } catch {
      toast({ variant: 'error', header: t('hppSettings.saveError') })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Box flexCol gap={32} className="max-w-lg">
        <Radio
          label={t('hppSettings.paymentMethodLabel')}
          name="paymentMethod"
          options={radioOptions}
          value={selectedPaymentMethod}
          onChange={value => setValue('paymentMethod', value as PaymentMethodFormValue)}
          border={false}
        />

        {showWallets && (
          <Box flexCol gap={12}>
            <Input
              label={t('hppSettings.walletsLabel')}
              LabelButton={
                <Badge
                  testId="wallet-count-badge"
                  aria-label={t('hppSettings.countSelected', { count: selectedWalletCount })}
                >
                  {selectedWalletCount}
                </Badge>
              }
              name="walletSearch"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={t('hppSettings.searchPlaceholder')}
            />
            <MultiSelectList
              multiSelectListDetails={{
                headerLabel: t('hppSettings.selectAllLabel'),
                testId: 'hpp-wallet-providers',
                sections: sectionsWithCallbacks,
                onSelectAllChange: handleSelectAllChange,
                noSectionItemsMessage: t('hppSettings.noResults'),
              }}
              hasBorder={false}
            />
          </Box>
        )}

        <Button type="submit" loading={isSubmitting} full>
          {t('hppSettings.save')}
        </Button>
      </Box>
    </form>
  )
}
